import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Platform,
  Pressable,
  ScrollView,
  StyleSheet,
  TextInput,
} from 'react-native';
import { useToast } from 'react-native-toast-notifications';

import Avatar from '@components/Avatar/Avatar';
import { Box, Text } from '@components/Restyle';
import ActivityIndicatorLoading from '@components/shared/ActivityIndicatorLoading';
import {
  Chat,
  Document,
  File,
  useForwardDocumentsMutation,
  useListChatPaginationIdentifiersForwardQuery,
  useSearchChatsQuery,
} from '@graphql/generated';
import { useDebouncedSearch } from '@hooks/useDebouncedSearch';
import useMe from '@hooks/useMe';
import Images from '@themes/images';
import theme from '@themes/theme';

// Local chat row shape — mirrors the subset of ContactType the row needs.
type ChatRow = {
  id: string;
  name: string;
  chatAvatar?: File | null;
  avatarUrl?: string | null;
  initials: string;
  isGroupChat: boolean;
  isSelfChat: boolean;
  groupColorSeed?: string;
};

interface Props {
  attachments: Document[];
  onComplete: () => void;
  onCancel: () => void;
}

export const ChatImageForwardPicker: React.FC<Props> = ({
  attachments,
  onComplete,
  onCancel,
}) => {
  const { me } = useMe();
  const [forwardDocuments] = useForwardDocumentsMutation();
  const { t } = useTranslation('models');
  const toast = useToast();

  const [search, setSearch] = useState('');
  // useSearchChatsQuery below is reactive (driven by its `term` variable, not
  // an imperative call), so there's no side-effecting search executor to
  // hand the hook — the query re-fires on its own whenever gatedTerm changes
  // (TA-10169).
  const { gatedTerm, hasSearched, isBelowThreshold, isPending } =
    useDebouncedSearch(search, () => {});

  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [submitting, setSubmitting] = useState(false);

  const { data: paginated, loading: paginatedLoading } =
    useListChatPaginationIdentifiersForwardQuery({
      variables: { first: 30, after: null },
      fetchPolicy: 'no-cache',
    });
  const { data: searched, loading: searchLoading } = useSearchChatsQuery({
    variables: { term: gatedTerm, first: 30 },
    skip: !gatedTerm,
    fetchPolicy: 'cache-and-network',
  });

  // hasSearched-gated: Apollo retains the previous response's `data` even
  // once the query stops re-firing below threshold — reading it unguarded
  // would leak a stale search's chats in as if live. Also gated during the
  // pending window between a keystroke and the debounce settling so a
  // previous above-threshold search's rows can't stay tappable while a new
  // term is still debouncing (TA-10169).
  const rows: ChatRow[] = useMemo(() => {
    const searchEdges = hasSearched ? searched?.searchChats?.edges : [];
    const edges = isBelowThreshold
      ? paginated?.listChatsPagination?.edges
      : searchEdges;
    return (edges ?? [])
      .filter((e) => e?.node)
      .map((e) => {
        const c = e!.node as Chat;
        const otherUser =
          !c.isGroupChat && c.users
            ? c.users.find((u) => u.id !== me?.id)
            : undefined;
        const isSelfChat =
          !c.isGroupChat && !!me?.id && !!c.users?.length && !otherUser;
        let displayName: string;
        if (c.name) displayName = c.name;
        else if (isSelfChat)
          displayName = t('chat.imageViewer.myDisplayName', {
            name: me?.name ?? '',
          });
        else displayName = otherUser?.name ?? t('chat.imageViewer.chat');
        const chatAvatar = isSelfChat
          ? ((me?.avatar as unknown as File) ?? null)
          : ((c.avatar as unknown as File) ??
            (otherUser?.avatar as unknown as File) ??
            null);
        return {
          id: c.id,
          name: displayName,
          chatAvatar,
          avatarUrl: c.avatarUrl ?? null,
          initials: (displayName.charAt(0) || '?').toUpperCase(),
          isGroupChat: c.isGroupChat,
          isSelfChat,
          groupColorSeed: c.isGroupChat ? c.name?.trim() : undefined,
        } as ChatRow;
      });
  }, [paginated, searched, isBelowThreshold, hasSearched, me, t]);

  // Safe merged-loading form: gating the whole expression on
  // !isBelowThreshold first (rather than `queryLoading || isPending || ...`)
  // stops a request still resolving right as the term drops below threshold
  // from leaking a stale spinner through in browse mode (TA-10169).
  const isSearchLoading =
    !isBelowThreshold && (searchLoading || isPending || !hasSearched);
  // Shows a spinner in browse mode (no search term) while the initial page of
  // chats is still loading — prevents an empty list on slow networks (TA-10284).
  const isBrowseLoading = isBelowThreshold && paginatedLoading;

  const toggle = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleForward = async () => {
    if (submitting || selected.size === 0 || !me?.id) return;
    setSubmitting(true);
    try {
      // errorPolicy is 'all' for every mutation in this app (useApolloClient.tsx),
      // so a GraphQL-level failure resolves normally instead of rejecting —
      // `errors` (not `data.forwardDocuments.errors`, a separate payload field)
      // is where that shows up, and `data.forwardDocuments` can be null even
      // though nothing threw. Checking only `data` previously left both the
      // toast AND onComplete() silently skipped on any such failure.
      const { data, errors } = await forwardDocuments({
        variables: {
          documentIds: attachments.map((d) => d.id),
          targetChatIds: Array.from(selected),
        },
      });
      setSubmitting(false);
      if (
        data?.forwardDocuments &&
        !data.forwardDocuments.errors?.length &&
        !errors?.length
      ) {
        toast.show(t('chat.imageViewer.forwarded'), { duration: 3000 });
        onComplete();
        return;
      }
      console.log('forwardDocuments did not succeed', {
        errors,
        payloadErrors: data?.forwardDocuments?.errors,
      });
      toast.show(t('chat.imageViewer.forwardFailed'), { duration: 3000 });
    } catch (e) {
      setSubmitting(false);
      console.log('forwardDocuments threw', e);
      toast.show(t('chat.imageViewer.forwardFailed'), { duration: 3000 });
    }
  };

  const disabled = selected.size === 0 || submitting;

  return (
    <>
      {/* Search row */}
      <Box flexDirection='row' alignItems='center' padding='m' gap='s'>
        <Box
          flex={1}
          flexDirection='row'
          alignItems='center'
          gap='xs'
          paddingHorizontal='s'
          borderRadius='xs'
          height={40}
          style={{ backgroundColor: theme.colors.scrimDark04 }}>
          <Images.Search width={16} height={16} color={theme.colors.grey04} />
          <TextInput
            style={searchInputStyle}
            value={search}
            onChangeText={setSearch}
            placeholder={t('chat.imageViewer.search')}
            placeholderTextColor={theme.colors.grey04}
            autoFocus
          />
        </Box>
        <Pressable
          onPress={onCancel}
          accessibilityLabel={t('chat.imageViewer.cancelForward')}>
          <Text variant='labelLarge' color='textSecondary'>
            {t('chat.imageViewer.cancel')}
          </Text>
        </Pressable>
      </Box>
      <Box
        height={StyleSheet.hairlineWidth}
        style={{ backgroundColor: theme.colors.scrimDark10 }}
      />

      {/* Contact list */}
      <ScrollView style={{ maxHeight: 360 }}>
        <Text
          variant='metadata'
          color='textSecondary'
          paddingHorizontal='m'
          paddingTop='s'
          paddingBottom='xs'>
          {t('chat.imageViewer.allChats')}
        </Text>
        {(isSearchLoading || isBrowseLoading) && <ActivityIndicatorLoading />}
        {!isSearchLoading &&
          !isBrowseLoading &&
          rows.map((row) => {
            const isChecked = selected.has(row.id);
            return (
              <Pressable
                key={row.id}
                style={({ pressed, hovered }: any) => [
                  rowStyle,
                  (pressed || hovered) && rowHoverStyle,
                ]}
                onPress={() => toggle(row.id)}
                accessibilityLabel={t(
                  isChecked
                    ? 'chat.imageViewer.deselectName'
                    : 'chat.imageViewer.selectName',
                  { name: row.name }
                )}>
                <Box
                  width={20}
                  height={20}
                  borderRadius='s'
                  borderWidth={1.5}
                  alignItems='center'
                  justifyContent='center'
                  flexShrink={0}
                  style={[
                    { borderColor: theme.colors.scrimDark20 },
                    isChecked && {
                      backgroundColor: theme.colors.greenSecondary,
                      borderColor: theme.colors.greenSecondary,
                    },
                  ]}>
                  {isChecked && (
                    <Images.Check
                      width={12}
                      height={12}
                      color={theme.colors.white}
                    />
                  )}
                </Box>
                <Avatar
                  avatarType='chat'
                  id={row.chatAvatar?.id ?? row.id}
                  label={row.name}
                  avatar={row.chatAvatar ?? undefined}
                  avatarUrl={row.avatarUrl}
                  width={44}
                  height={44}
                  isMultiple={row.isGroupChat}
                  groupColorSeed={
                    row.isGroupChat ? row.groupColorSeed : undefined
                  }
                  disableAvatarPress
                />
                <Text
                  variant='body'
                  color='textSecondary'
                  flex={1}
                  numberOfLines={1}>
                  {row.name}
                </Text>
              </Pressable>
            );
          })}
        <Box height={theme.spacing.xs} />
      </ScrollView>

      {/* Submit bar */}
      <Box
        paddingHorizontal='m'
        paddingVertical='s'
        borderTopWidth={StyleSheet.hairlineWidth}
        style={{ borderTopColor: theme.colors.scrimDark10 }}>
        <Pressable
          disabled={disabled}
          style={({ pressed, hovered }: any) => [
            submitBtnStyle,
            disabled && { backgroundColor: theme.colors.scrimDark08 },
            !disabled && (pressed || hovered) && { opacity: 0.85 },
          ]}
          onPress={handleForward}
          accessibilityLabel={t('chat.imageViewer.forwardToSelectedChats')}>
          <Text
            variant='labelLarge'
            color={disabled ? undefined : 'white'}
            style={disabled ? { color: theme.colors.scrimDark30 } : undefined}>
            {submitting
              ? t('chat.imageViewer.forwarding')
              : t('chat.imageViewer.forward')}
          </Text>
        </Pressable>
      </Box>
    </>
  );
};

// Inline style snippets — only for properties Restyle/Box don't expose
// natively (raw outlineStyle for web, or composite shorthand styles applied
// to Pressable rather than Box).
const searchInputStyle = {
  flex: 1,
  fontSize: 15,
  color: theme.colors.black,
  ...(Platform.OS === 'web' ? ({ outlineStyle: 'none' } as any) : {}),
};

const rowStyle = {
  flexDirection: 'row' as const,
  alignItems: 'center' as const,
  gap: theme.spacing.s,
  paddingHorizontal: theme.spacing.m,
  paddingVertical: theme.spacing.s,
};
const rowHoverStyle = { backgroundColor: theme.colors.scrimDark04 };

const submitBtnStyle = {
  backgroundColor: theme.colors.greenSecondary,
  borderRadius: theme.borderRadii.xs,
  paddingVertical: theme.spacing.s,
  alignItems: 'center' as const,
};
